import {
    ButtonSecondary,
    Colors,
    CYCLE_PAGE_LAYOUT,
    Heading,
    Icon,
    PageContainer,
    Text,
} from '@amelio/ui-library';

import { ChangesCard } from './ChangesCard';
import { CycleFrameCard } from './CycleFrameCard';
import { EvaluationsCard } from './EvaluationsCard';
import { ScheduleCard } from './ScheduleCard';

import type { LaunchSummaryProps } from './LaunchSummary.types';
import type { TranslationKey } from '@/i18n-key';
import type { LaunchReviewTranslationKeys } from '@/pages/shared/ScheduleCreation/components/LaunchReview/LaunchReview';

import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { useAmelioTranslations } from '@/hooks';
import { CompletedCycleNotice } from '@/pages/CycleEvaluation/components/CompletedCycleNotice/CompletedCycleNotice';
import { LaunchReview } from '@/pages/shared/ScheduleCreation/components/LaunchReview/LaunchReview';

export type {
    EvaluationSummaryRow,
    LaunchSummaryProps,
    ScheduleSummaryRow,
} from './LaunchSummary.types';

const PAGE_TITLE_TYPE = 'text-[36px] leading-[40px] font-semibold';
const PAGE_SUBTITLE_TYPE = 'mt-[8px] text-[18px] leading-[28px]';
const SUMMARY_TITLE_TYPE = 'text-[24px] leading-[32px] font-semibold';

export const LaunchSummary = ({
    isLoading,
    cycleName,
    periodLabel,
    resolvedEmployeeCount,
    evaluations,
    schedule,
    isLaunching,
    isLaunchBlocked,
    isConfigurationLocked,
    isAlreadyLaunched,
    changes,
    hasChangesToApply,
    launchNote,
    onBack,
    onLaunch,
}: LaunchSummaryProps) => {
    const { t } = useAmelioTranslations();

    const invitationTitleKey: TranslationKey = isAlreadyLaunched
        ? 'cycleEvaluationSchedule.launchSummary.launchReview.updateTitle'
        : 'cycleEvaluationSchedule.launchSummary.launchReview.title';

    const translationKeys: LaunchReviewTranslationKeys = {
        titleKey: isConfigurationLocked ? undefined : invitationTitleKey,
        ctaKey: isAlreadyLaunched
            ? 'cycleEvaluationSchedule.launchSummary.changes.applyCta'
            : 'cycleEvaluationSchedule.launchSummary.launchReview.cta',
    };

    // A launched cycle with nothing to apply withdraws its CTA: the post would only re-send the
    // configuration already running. A guard block keeps precedence, its reasons matter more.
    const hasNothingToApply = isAlreadyLaunched && !isLaunchBlocked && !hasChangesToApply;

    const getLaunchDisabledReason = (): string | undefined => {
        if (isConfigurationLocked) {
            return t('cycleEvaluationGroupStepper.readOnlyNotice');
        }
        return hasNothingToApply
            ? t('cycleEvaluationSchedule.launchSummary.changes.none')
            : undefined;
    };
    const launchDisabledReason = getLaunchDisabledReason();

    const pageHeader = (
        <div className={'pb-8'}>
            <Heading
                level={1}
                color={Colors.SLATE}
                className={PAGE_TITLE_TYPE}
            >
                {t('cycleEvaluationGroupStepper.pageTitle', { name: cycleName })}
            </Heading>

            <Text
                color={Colors.STEP_RAIL_MUTED}
                className={PAGE_SUBTITLE_TYPE}
            >
                {t('cycleEvaluationGroupStepper.pageSubtitle')}
            </Text>
        </div>
    );

    if (isLoading) {
        return (
            <PageContainer
                className={[
                    CYCLE_PAGE_LAYOUT.maxWidth,
                    'cycle-page-top-gap',
                    'flex w-full flex-col',
                ].join(' ')}
            >
                {pageHeader}

                <Skeleton className={'flex flex-col gap-4'}>
                    <Bone className={'h-28 w-full'} />

                    <Bone className={'h-28 w-full'} />

                    <Bone className={'h-40 w-full'} />
                </Skeleton>
            </PageContainer>
        );
    }

    return (
        <PageContainer
            className={[
                CYCLE_PAGE_LAYOUT.maxWidth,
                'cycle-page-top-gap',
                'flex w-full flex-col',
            ].join(' ')}
            dataTestId={'launch-summary'}
        >
            {pageHeader}

            <div className={'flex w-full flex-col gap-6'}>
                <div>
                    <Heading
                        level={3}
                        className={`flex items-center gap-[8px] ${SUMMARY_TITLE_TYPE}`}
                    >
                        {/* Bare flex children stretch an intrinsic-size-less SVG to the row's
                            full width — the same guard `CardHeader` already wraps its own icon
                            in. */}
                        <span className={'shrink-0'}>
                            <Icon
                                name={'lucide-sparkles'}
                                color={Colors.STEP_RAIL_VIOLET}
                                size={20}
                            />
                        </span>

                        {t('cycleEvaluationSchedule.launchSummary.header.title')}
                    </Heading>

                    <Text
                        color={Colors.STEP_RAIL_MUTED}
                        className={'mt-[4px]'}
                    >
                        {t(
                            isAlreadyLaunched
                                ? 'cycleEvaluationSchedule.launchSummary.changes.subtitle'
                                : 'cycleEvaluationSchedule.launchSummary.header.subtitle',
                        )}
                    </Text>
                </div>

                {changes && <ChangesCard changes={changes} />}

                <CycleFrameCard
                    cycleName={cycleName}
                    periodLabel={periodLabel}
                    resolvedEmployeeCount={resolvedEmployeeCount}
                />

                <EvaluationsCard evaluations={evaluations} />

                <ScheduleCard schedule={schedule} />

                <div className={'flex justify-end gap-2'}>
                    <ButtonSecondary
                        className={'min-w-40'}
                        dataTestId={'launch-summary-back-button'}
                        onClick={onBack}
                        disabled={isLaunching}
                        leftIcon={
                            <Icon
                                name={'arrow-arrow-left'}
                                className={'mr-2'}
                            />
                        }
                    >
                        {t('common.back')}
                    </ButtonSecondary>
                </div>

                <LaunchReview
                    onLaunchReview={onLaunch}
                    isLaunching={isLaunching}
                    disabled={isLaunchBlocked || hasNothingToApply}
                    disabledReason={launchDisabledReason}
                    translationKeys={translationKeys}
                    note={launchNote}
                />

                {isConfigurationLocked && (
                    <div className={'flex justify-center'}>
                        <CompletedCycleNotice dataTestId={'launch-summary-read-only-notice'} />
                    </div>
                )}

                {isLaunchBlocked && !launchDisabledReason && (
                    <div data-testid={'launch-summary-blocked-message'}>
                        <Text
                            color={Colors.ERROR}
                            className={'text-center text-sm'}
                        >
                            {t('cycleEvaluationSchedule.launchSummary.launchReview.blockedMessage')}
                        </Text>
                    </div>
                )}
            </div>
        </PageContainer>
    );
};
